/**
 * Sensor Fusion Acceleration Calculator
 *
 * Combines accelerometer, gyroscope, and GPS data for accurate acceleration
 * by correcting for device orientation (lean/pitch/yaw) and removing gravity.
 *
 * Problem: Raw accelerometer readings are in the bike's reference frame.
 * When leaned at 45°, "lateral" axis points partially toward gravity.
 * When pitching under braking, "longitudinal" axis picks up gravity.
 *
 * Solution:
 * 1. Track full 3D orientation using gyroscope (roll, pitch, yaw)
 * 2. Use accelerometer for absolute reference (prevents drift)
 * 3. Rotate accelerometer readings to earth/road frame
 * 4. Subtract gravity to get actual dynamic acceleration
 * 5. Use GPS as validation and fallback
 *
 * Gyroscope axes (standard orientation):
 *   X = Roll rate (lean left/right)
 *   Y = Pitch rate (nose up/down)
 *   Z = Yaw rate (turning left/right)
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { AccelerationPoint } from '../domain/telemetry/processed/Acceleration'
import { AccelerometerReading } from '../domain/telemetry/raw/AccelerometerReading'
import { GyroscopeReading } from '../domain/telemetry/raw/GyroscopeReading'
import { LeanAnglePoint } from '../domain/telemetry/processed/LeanAngle'
import { FrequencyAdaptiveConfig } from '../utils/FrequencyAdaptiveConfig'
import { smoothValuesGaussian } from '../utils/smoothingUtils'

interface AccelerationFusionConfig {
  /**
   * Weight for GPS validation (0-1)
   * When sensor and GPS values diverge, blend GPS estimate
   */
  gpsValidationWeight: number

  /**
   * Threshold (m/s²) for GPS correction
   * Only apply GPS correction if difference exceeds this
   */
  gpsCorrectThreshold: number

  /**
   * Minimum speed (km/h) for GPS lateral calculation
   */
  minSpeedForGpsLateral: number

  /**
   * Apply smoothing to output
   */
  smoothOutput: boolean
}

const DEFAULT_CONFIG: AccelerationFusionConfig = {
  gpsValidationWeight: 0.2,
  gpsCorrectThreshold: 2.0, // m/s²
  minSpeedForGpsLateral: 15,
  smoothOutput: true,
}

interface Orientation3D {
  rollDeg: number // lean angle (left/right)
  pitchDeg: number // nose up/down
  yawDeg: number // heading (not critical for gravity removal)
}

/**
 * Calculate pitch angle from accelerometer when bike is relatively stable
 * Pitch = atan2(accelX, sqrt(accelY² + accelZ²))
 */
function getPitchFromAccelerometer(
  accelX: number,
  accelY: number,
  accelZ: number
): number {
  const horizontalMag = Math.sqrt(accelY * accelY + accelZ * accelZ)
  if (horizontalMag < 0.1) return 0 // Avoid division issues

  const pitchRad = Math.atan2(-accelX, horizontalMag)
  return (pitchRad * 180) / Math.PI
}

/**
 * Track 3D orientation using complementary filter.
 * Combines gyroscope (fast, but drifts) with accelerometer (absolute reference)
 */
function track3DOrientation(
  accelerometer: AccelerometerReading[],
  gyroscope: GyroscopeReading[],
  leanAngle: LeanAnglePoint[],
  gpsPoints: GpsPoint[]
): Orientation3D[] {
  const GYRO_WEIGHT = 0.97 // High-pass for gyro
  const ACCEL_WEIGHT = 1 - GYRO_WEIGHT // Low-pass for accel

  const orientations: Orientation3D[] = []

  // Initialize
  let currentRoll = 0
  let currentPitch = 0
  let currentYaw = 0

  let gyroIndex = 0
  let accelIndex = 0

  for (let i = 0; i < gpsPoints.length; i++) {
    const point = gpsPoints[i]
    const timestamp = point.timestamp

    // Use pre-calculated lean angle for roll (it's already sensor-fused)
    const preCalcRoll = leanAngle[i]?.leanAngleDegrees ?? currentRoll

    // Find closest accelerometer for pitch reference
    while (
      accelIndex < accelerometer.length - 1 &&
      accelerometer[accelIndex + 1].timestamp <= timestamp
    ) {
      accelIndex++
    }
    const accelReading = accelerometer[accelIndex]

    // Find gyroscope readings between last point and this point
    const prevTimestamp = i > 0 ? gpsPoints[i - 1].timestamp : timestamp

    // Integrate gyroscope for pitch
    while (
      gyroIndex < gyroscope.length &&
      gyroscope[gyroIndex].timestamp <= timestamp
    ) {
      const gyro = gyroscope[gyroIndex]

      // Convert to deg/s if needed
      const factor = gyro.unit === 'rad/s' ? 180 / Math.PI : 1
      const pitchRate = gyro.y * factor // Y-axis is pitch rate
      const yawRate = gyro.z * factor // Z-axis is yaw rate

      // Calculate dt
      const prevGyroTime =
        gyroIndex > 0 ? gyroscope[gyroIndex - 1].timestamp : gyro.timestamp
      const dt = (gyro.timestamp.getTime() - prevGyroTime.getTime()) / 1000

      if (dt > 0 && dt < 0.5) {
        // Integrate pitch from gyro
        currentPitch += pitchRate * dt
        currentYaw += yawRate * dt
      }

      gyroIndex++
    }

    // Fuse with accelerometer for pitch (prevents drift)
    if (accelReading) {
      const factor = accelReading.unit === 'g' ? 9.81 : 1
      const accelPitch = getPitchFromAccelerometer(
        accelReading.x * factor,
        accelReading.y * factor,
        accelReading.z * factor
      )

      // Complementary filter for pitch
      currentPitch = GYRO_WEIGHT * currentPitch + ACCEL_WEIGHT * accelPitch
    }

    // Clamp pitch to reasonable motorcycle range (-20° to +20°)
    currentPitch = Math.max(-20, Math.min(20, currentPitch))

    // Roll comes from pre-calculated lean angle
    currentRoll = preCalcRoll

    orientations.push({
      rollDeg: currentRoll,
      pitchDeg: currentPitch,
      yawDeg: currentYaw,
    })
  }

  return orientations
}

/**
 * Transform accelerometer readings from bike frame to earth frame,
 * accounting for both lean (roll) and pitch angles.
 *
 * Bike frame:
 *   X = forward (longitudinal)
 *   Y = right (lateral)
 *   Z = down (vertical)
 *
 * Earth frame (what we want):
 *   X = forward along road
 *   Y = right along road (horizontal)
 *   Z = down (gravity direction)
 *
 * Rotation order: First roll (lean), then pitch
 * - Roll (θ): Rotation around X-axis (lean left/right)
 * - Pitch (φ): Rotation around Y-axis (nose up/down)
 */
function transformToEarthFrame3D(
  accelX: number, // longitudinal (bike frame)
  accelY: number, // lateral (bike frame)
  accelZ: number, // vertical (bike frame)
  rollDeg: number, // lean angle
  pitchDeg: number // pitch angle
): { earthX: number; earthY: number; earthZ: number } {
  const rollRad = (rollDeg * Math.PI) / 180
  const pitchRad = (pitchDeg * Math.PI) / 180

  const cosRoll = Math.cos(rollRad)
  const sinRoll = Math.sin(rollRad)
  const cosPitch = Math.cos(pitchRad)
  const sinPitch = Math.sin(pitchRad)

  // Combined rotation matrix R = Ry(pitch) * Rx(roll)
  // This transforms from bike frame to earth frame

  // Step 1: Apply roll rotation (around X-axis)
  const y1 = accelY * cosRoll - accelZ * sinRoll
  const z1 = accelY * sinRoll + accelZ * cosRoll
  const x1 = accelX

  // Step 2: Apply pitch rotation (around Y-axis)
  const earthX = x1 * cosPitch + z1 * sinPitch
  const earthY = y1
  const earthZ = -x1 * sinPitch + z1 * cosPitch

  return { earthX, earthY, earthZ }
}

/**
 * Remove gravity from earth-frame acceleration to get dynamic acceleration only
 *
 * In earth frame, gravity is always [0, 0, -9.81] (pointing down)
 * Actual acceleration = measured - gravity
 */
function removeGravity(
  earthX: number,
  earthY: number,
  earthZ: number
): { dynamicX: number; dynamicY: number; dynamicZ: number } {
  const GRAVITY = 9.81

  return {
    dynamicX: earthX, // No gravity component in forward direction
    dynamicY: earthY, // No gravity component in lateral direction (horizontal)
    dynamicZ: earthZ + GRAVITY, // Remove downward gravity (Z is down, gravity is -9.81)
  }
}

function findClosestReading<T extends { timestamp: Date }>(
  readings: T[],
  targetTime: Date,
  lastIndex: number = 0
): { reading: T | undefined; index: number } {
  if (readings.length === 0) return { reading: undefined, index: 0 }

  let closest = readings[lastIndex]
  let closestIndex = lastIndex
  let minDiff = Math.abs(
    readings[lastIndex].timestamp.getTime() - targetTime.getTime()
  )

  for (let i = lastIndex + 1; i < readings.length; i++) {
    const diff = Math.abs(
      readings[i].timestamp.getTime() - targetTime.getTime()
    )
    if (diff < minDiff) {
      minDiff = diff
      closest = readings[i]
      closestIndex = i
    } else if (diff > minDiff) {
      break
    }
  }

  return { reading: closest, index: closestIndex }
}

function calculateGpsLongitudinal(
  current: GpsPoint,
  previous: GpsPoint | undefined
): number {
  if (!previous) return 0

  const deltaSpeed = (current.speedKmh - previous.speedKmh) / 3.6 // to m/s
  const deltaTime =
    (current.timestamp.getTime() - previous.timestamp.getTime()) / 1000

  if (deltaTime <= 0 || deltaTime > 1) return 0 // Sanity check

  return deltaSpeed / deltaTime
}

/**
 * Calculate acceleration using sensor fusion.
 *
 * Combines accelerometer + gyroscope data with lean angle to produce
 * gravity-corrected acceleration values in the earth/road frame.
 *
 * When gyroscope is available:
 * - Tracks full 3D orientation (roll + pitch + yaw)
 * - Pitch correction is critical for accurate braking/acceleration values
 * - During hard braking, bike pitches forward ~5-10°, contaminating readings
 *
 * @param gpsPoints - Processed GPS points
 * @param accelerometer - Raw accelerometer readings
 * @param leanAngle - Pre-calculated lean angle (from sensor fusion or other method)
 * @param frequencyConfig - Frequency configuration for smoothing
 * @param gyroscope - Optional raw gyroscope readings for 3D orientation
 * @param config - Fusion configuration
 */
export function calculateAccelerationWithSensorFusion(
  gpsPoints: GpsPoint[],
  accelerometer: AccelerometerReading[],
  leanAngle: LeanAnglePoint[],
  frequencyConfig: FrequencyAdaptiveConfig,
  gyroscope: GyroscopeReading[] = [],
  config: Partial<AccelerationFusionConfig> = {}
): AccelerationPoint[] {
  const cfg = { ...DEFAULT_CONFIG, ...config }

  if (gpsPoints.length === 0) return []

  // Calculate 3D orientation if gyroscope is available
  const has3DOrientation = gyroscope.length > 0
  const orientations = has3DOrientation
    ? track3DOrientation(accelerometer, gyroscope, leanAngle, gpsPoints)
    : null

  let accelIndex = 0
  const results: AccelerationPoint[] = []

  for (let i = 0; i < gpsPoints.length; i++) {
    const point = gpsPoints[i]

    // Get orientation - use 3D if available, otherwise just lean (roll)
    const roll =
      orientations?.[i]?.rollDeg ?? leanAngle[i]?.leanAngleDegrees ?? 0
    const pitch = orientations?.[i]?.pitchDeg ?? 0

    // Get closest accelerometer reading
    const accelResult = findClosestReading(
      accelerometer,
      point.timestamp,
      accelIndex
    )
    accelIndex = accelResult.index
    const accelReading = accelResult.reading

    let longitudinal = 0
    let lateral = 0
    let vertical = 0

    if (accelReading) {
      // Convert to m/s² if needed
      const factor = accelReading.unit === 'g' ? 9.81 : 1
      const rawX = accelReading.x * factor
      const rawY = accelReading.y * factor
      const rawZ = accelReading.z * factor

      // Step 1: Transform to earth frame (accounting for roll AND pitch)
      const earth = transformToEarthFrame3D(rawX, rawY, rawZ, roll, pitch)

      // Step 2: Remove gravity to get dynamic acceleration
      const dynamic = removeGravity(earth.earthX, earth.earthY, earth.earthZ)

      longitudinal = dynamic.dynamicX
      lateral = dynamic.dynamicY
      vertical = dynamic.dynamicZ

      // Step 3: GPS validation for longitudinal (if deviation is significant)
      const gpsLongitudinal = calculateGpsLongitudinal(
        point,
        i > 0 ? gpsPoints[i - 1] : undefined
      )

      const longDeviation = Math.abs(longitudinal - gpsLongitudinal)
      if (longDeviation > cfg.gpsCorrectThreshold) {
        // Blend with GPS estimate
        longitudinal =
          (1 - cfg.gpsValidationWeight) * longitudinal +
          cfg.gpsValidationWeight * gpsLongitudinal
      }
    } else {
      // No accelerometer - fall back to GPS-derived (basic)
      longitudinal = calculateGpsLongitudinal(
        point,
        i > 0 ? gpsPoints[i - 1] : undefined
      )
      // Lateral would need turn radius calculation (handled elsewhere)
    }

    results.push({
      longitudinalMs2: Math.round(longitudinal * 100) / 100,
      lateralMs2: Math.round(lateral * 100) / 100,
      verticalMs2: Math.round(vertical * 100) / 100,
      timestamp: point.timestamp,
      distanceMeters: point.distanceMeters,
      elapsedMs: point.elapsedMs,
      source: has3DOrientation
        ? ('sensor-fusion' as const)
        : ('accelerometer' as const),
    })
  }

  // Apply smoothing if configured
  if (cfg.smoothOutput && results.length > 10) {
    const smoothingWindow = Math.round(5 * frequencyConfig.frequencyScale)

    const longValues = results.map((p) => p.longitudinalMs2)
    const latValues = results.map((p) => p.lateralMs2)

    const smoothedLong = smoothValuesGaussian(
      longValues,
      smoothingWindow,
      frequencyConfig.smoothingPasses
    )
    const smoothedLat = smoothValuesGaussian(
      latValues,
      smoothingWindow,
      frequencyConfig.smoothingPasses
    )

    results.forEach((point, idx) => {
      point.longitudinalMs2 = Math.round(smoothedLong[idx] * 100) / 100
      point.lateralMs2 = Math.round(smoothedLat[idx] * 100) / 100
    })
  }

  return results
}
